import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL, API_ENDPOINTS } from '../config/api';
import { getAuthHeaders } from '../utils/auth';
import './StartTrialModal.css';

export default function StartTrialModal({ isOpen, onClose, isTrial = true, initialData = null }) {
  const RESUME_TEXT_CACHE_KEY = 'resume_text_cache';
  const LEGACY_RESUME_KEY = 'resume_text_response';
  const GPT_MODELS_CACHE_KEY = 'gpt_models_cache_v1';
  const GPT_MODELS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  const TRIAL_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes between trial sessions (starts after trial ENDS)
  const TRIAL_LAST_END_KEY = 'bk_last_trial_ended_at';
  const navigate = useNavigate();
  const [step, setStep] = useState(initialData?.step || 1);
  const [trialCooldownUntil, setTrialCooldownUntil] = useState(null);
  const [cooldownRemainingMs, setCooldownRemainingMs] = useState(0);
  const normalizeModelName = (name) => {
    if (!name) return 'GPT-4.0 (Faster)';
    const lower = name.toLowerCase();
    if (lower.includes('gpt-5')) return 'GPT-4.0 (Faster)';
    return name;
  };
  const [formData, setFormData] = useState(initialData ? {
    company: initialData.company || '',
    jobDescription: initialData.jobDescription || '',
    language: initialData.language || 'en',
    aiModel: normalizeModelName(initialData.aiModel) || 'GPT-4.0 (Faster)',
    answerMode: initialData.answerMode || 'medium',
    resumeId: initialData.resumeId || null
  } : {
    company: '',
    jobDescription: '',
    language: 'en',
    aiModel: 'GPT-4.0 (Faster)',
    answerMode: 'medium',
    resumeId: null
  });

  const [resumes, setResumes] = useState([]);
  // Validation errors state
  const [errors, setErrors] = useState({ company: '', resume: '' });
  // Screen share error state
  const [screenShareError, setScreenShareError] = useState('');
  // GPT models state
  const [gptModels, setGptModels] = useState([]);
  const gptFetchInFlightRef = useRef(false);
  const trialSessionIncrementedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      // Clean up old/unused cache key when modal opens
      if (localStorage.getItem(LEGACY_RESUME_KEY)) {
        localStorage.removeItem(LEGACY_RESUME_KEY);
      }
      // Load resumes from localStorage
      // Removed loadResumes call

      // If a trial recently ENDED, restore the cooldown timer
      if (isTrial) {
        const lastEndRaw = localStorage.getItem(TRIAL_LAST_END_KEY);
        const lastEnd = lastEndRaw ? Number(lastEndRaw) : 0;
        if (!Number.isNaN(lastEnd) && lastEnd > 0) {
          const elapsed = Date.now() - lastEnd;
          if (elapsed < TRIAL_COOLDOWN_MS) {
            const remaining = TRIAL_COOLDOWN_MS - elapsed;
            setTrialCooldownUntil(Date.now() + remaining);
            setCooldownRemainingMs(remaining);
          }
        }
      }
    }
  }, [TRIAL_COOLDOWN_MS, isOpen, isTrial]);

  // Live countdown for trial cooldown
  useEffect(() => {
    if (!trialCooldownUntil) return;

    const tick = () => {
      const remaining = Math.max(0, trialCooldownUntil - Date.now());
      setCooldownRemainingMs(remaining);
      if (remaining <= 0) {
        setTrialCooldownUntil(null);
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [trialCooldownUntil]);

  // Restore saved state if provided
  useEffect(() => {
    if (initialData) {
      // console.log('Restoring modal state:', initialData);
      setStep(initialData.step || 1);
      setFormData({
        company: initialData.company || '',
        jobDescription: initialData.jobDescription || '',
        language: initialData.language || 'en',
        aiModel: normalizeModelName(initialData.aiModel) || 'GPT-4.0 (Faster)',
        answerMode: initialData.answerMode || 'medium',
        resumeId: initialData.resumeId || null
      });
    }
  }, [initialData]);

  // Fetch GPT models when modal opens and step is 1
  useEffect(() => {
    if (isOpen && step === 1) {
      const fetchModels = async () => {
        // Prevent duplicate calls while a fetch is in flight
        if (gptFetchInFlightRef.current) return;

        // Try cached models first
        try {
          const cachedRaw = localStorage.getItem(GPT_MODELS_CACHE_KEY);
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw);
            if (cached?.models && Array.isArray(cached.models) && Date.now() - (cached.cachedAt || 0) < GPT_MODELS_CACHE_TTL_MS) {
              setGptModels(cached.models);
              return;
            }
          }
        } catch (cacheErr) {
          // ignore cache errors
        }

        try {
          gptFetchInFlightRef.current = true;
          const headers = getAuthHeaders();
          
          const res = await fetch(`${BACKEND_URL}/api/admin/gpt-models?activeOnly=true`, {
            credentials: 'include',
            headers
          });
          if (!res.ok) return;
          const data = await res.json();
          // Support both {models: [...]} and [...] response
          const models = Array.isArray(data) ? data : (Array.isArray(data.models) ? data.models : []);
          setGptModels(models);
          // Cache models with timestamp
          try {
            localStorage.setItem(GPT_MODELS_CACHE_KEY, JSON.stringify({ models, cachedAt: Date.now() }));
          } catch (cacheErr) {
            // ignore cache write errors
          }
        } catch (e) {
          setGptModels([]);
        } finally {
          gptFetchInFlightRef.current = false;
        }
      };
      fetchModels();
    }
  }, [isOpen, step]);

  // Fetch resumes from backend when modal opens and step is 3
  useEffect(() => {
    if (isOpen && step === 3) {
      const fetchResumes = async () => {
        try {
          const headers = getAuthHeaders();
          
          const res = await fetch(`${BACKEND_URL}/api/resumes`, {
            credentials: 'include',
            headers
          });
          if (!res.ok) return setResumes([]);
          const data = await res.json();
          const resumesArr = Array.isArray(data.resumes) ? data.resumes : [];
          setResumes(resumesArr);
        } catch (e) {
          setResumes([]);
        }
      };
      fetchResumes();
    }
  }, [isOpen, step]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing/selecting
    if (field === 'company' && errors.company) {
      setErrors(prev => ({ ...prev, company: '' }));
    }
    if (field === 'resumeId' && errors.resume) {
      setErrors(prev => ({ ...prev, resume: '' }));
    }
  };

  // Function to add descriptive labels to GPT models
  const getModelDisplayName = (modelName) => {
    const lowerModel = modelName.toLowerCase();
    
    if (lowerModel.includes('gpt-5') || lowerModel.includes('gpt5')) {
      return `${modelName} (Advanced)`;
    } else if (lowerModel.includes('4.1-mini') || lowerModel.includes('4-1-mini')) {
      return `${modelName} (Faster)`;
    } else if (lowerModel.includes('4.1') || lowerModel.includes('4-1')) {
      return `${modelName} (Smarter)`;
    } else if (lowerModel.includes('4o') || lowerModel.includes('gpt-4') && !lowerModel.includes('4.1')) {
      return `${modelName} (Faster)`;
    } else if (lowerModel.includes('3.5') || lowerModel.includes('3-5')) {
      return `${modelName} (Budget)`;
    } else if (lowerModel.includes('code') || lowerModel.includes('codex')) {
      return `${modelName} (Coders)`;
    }
    
    return modelName; // Return as-is if no match
  };

  const handleNext = async () => {
    if (step === 3 && (!formData.resumeId || isNaN(Number(formData.resumeId)))) {
      setErrors(prev => ({ ...prev, resume: 'Please select a resume to proceed' }));
      return;
    }
    if (step === 1) {
      if (!isStep1Valid) {
        setErrors(prev => ({ ...prev, company: 'Please enter a company name' }));
        return;
      }
      setErrors({ company: '', resume: '' });
      setStep(3); // Skip directly to resume selection
    } else if (step < 3) {
      setStep(step + 1);
    } else {
      // Final step - start the interview
      // Ensure resumeId is a number
      if (formData.resumeId) {
        setFormData(prev => ({ ...prev, resumeId: Number(prev.resumeId) }));
      }
      await handleStartInterview();
    }
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(1); // Go back to step 1 from resume selection
    } else if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      company: '',
      jobDescription: '',
      language: 'en',
      aiModel: 'GPT-4.0 (Faster)',
      answerMode: 'medium',
      resumeId: null
    });
    trialSessionIncrementedRef.current = false; // Reset for next trial
    onClose();
  };

  const handleAddResume = () => {
    // Store current modal state before navigating
    localStorage.setItem('trial_modal_state', JSON.stringify({ 
      ...formData, 
      step, 
      isTrial, 
      shouldReopen: true 
    }));
    handleClose();
    navigate('/upload');
  };

  const handleStartInterview = async () => {
    // Clear previous screen share error
    setScreenShareError('');
    
    // Enforce cooldown between trial sessions (cooldown is from when trial ENDED)
    if (isTrial) {
      const lastEndRaw = localStorage.getItem(TRIAL_LAST_END_KEY);
      const lastEnd = lastEndRaw ? Number(lastEndRaw) : 0;
      if (!Number.isNaN(lastEnd) && lastEnd > 0) {
        const elapsed = Date.now() - lastEnd;
        if (elapsed < TRIAL_COOLDOWN_MS) {
          const remainingMs = TRIAL_COOLDOWN_MS - elapsed;
          setTrialCooldownUntil(Date.now() + remainingMs);
          setCooldownRemainingMs(remainingMs);
          return;
        }
      }
    }

    // Store interview configuration
    // Ensure resumeId is a number
    const normalizedModel = normalizeModelName(formData.aiModel);
    const interviewConfig = { 
      ...formData, 
      aiModel: normalizedModel,
      resumeId: formData.resumeId ? Number(formData.resumeId) : null,
      isTrial: isTrial // Add isTrial flag to config
    };
    localStorage.setItem('interview_config', JSON.stringify(interviewConfig));

    // Increment trial session count when user starts trial (only once)
    if (isTrial && !trialSessionIncrementedRef.current) {
      trialSessionIncrementedRef.current = true;
      try {
        const headers = getAuthHeaders();

        await fetch(API_ENDPOINTS.USERS.INCREMENT_TRIAL_SESSION, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ platform: 'web' })
        });
        console.log('✅ Trial session count incremented');
      } catch (error) {
        console.error('❌ Failed to increment trial session count:', error);
        trialSessionIncrementedRef.current = false; // Reset on error to allow retry
        // Continue with interview even if increment fails
      }
    }
    
    // Call /api/resumes/text if resumeId is present
    if (interviewConfig.resumeId) {
      const resumeIdNum = Number(interviewConfig.resumeId);
      let cached = null;
      try {
        const raw = localStorage.getItem(RESUME_TEXT_CACHE_KEY);
        cached = raw ? JSON.parse(raw) : null;
      } catch (e) {
        cached = null;
      }

      if (cached?.resumeId === resumeIdNum && cached?.data?.text) {
        localStorage.setItem('resume_content', cached.data.text);
      } else {
        try {
          const headers = getAuthHeaders();
          
          const res = await fetch(`${BACKEND_URL}/api/resumes/text?resumeId=${encodeURIComponent(resumeIdNum)}`, {
            method: 'GET',
            credentials: 'include',
            headers
          });
          const data = await res.json();
          if (data && data.text) {
            localStorage.setItem('resume_content', data.text);
            localStorage.setItem(RESUME_TEXT_CACHE_KEY, JSON.stringify({
              resumeId: resumeIdNum,
              data,
              fetchedAt: Date.now()
            }));
          }
        } catch (e) {
          // Optionally handle error
          console.error('Error calling /api/resumes/text:', e);
        }
      }
    }
    
    // Check if user is on mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Check if getDisplayMedia is supported (not available on mobile)
    const isScreenShareSupported = navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function';
    
    // Skip screen sharing on mobile or if not supported
    if (isMobile || !isScreenShareSupported) {
      // Store flag that screen sharing was skipped
      localStorage.setItem('screen_share_skipped', 'true');
      // Note: Trial cooldown timestamp will be set when trial ENDS, not here
      
      // Navigate directly without screen sharing
      navigate('/chat', { replace: true });
      setTimeout(() => {
        handleClose();
      }, 100);
      return;
    }
    
    // Request screen sharing FIRST, before navigating (desktop only)
    try {
      // console.log('Requesting screen sharing before navigation...');
      
      // Store reference to current tab before opening dialog
      const currentTab = window;
      
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          cursor: 'always',
          // Allow window sharing (Teams/Zoom window stays active when switching tabs)
          displaySurface: 'window'
        },
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
          channelCount: 1
        },
        preferCurrentTab: false,
        surfaceSwitching: 'include',
        selfBrowserSurface: 'exclude'
      });
      
      // SUPER AGGRESSIVE immediate focus restoration
      // Execute synchronously multiple times
      for (let i = 0; i < 10; i++) {
        if (window.focus) window.focus();
        if (document.body && document.body.focus) document.body.focus();
      }
      
      // Use requestAnimationFrame for next frame focus
      for (let i = 0; i < 30; i++) {
        requestAnimationFrame(() => {
          if (window.focus) window.focus();
          if (document.body && document.body.focus) document.body.focus();
        });
      }
      
      // Very aggressive short interval refocus attempts
      const intervals = [];
      for (let i = 0; i <= 500; i += 5) {
        intervals.push(i);
      }
      intervals.forEach(delay => {
        setTimeout(() => {
          if (window.focus) window.focus();
          if (document.body && document.body.focus) document.body.focus();
        }, delay);
      });
      
      // Store the screen stream for the chat interface to pick up
      localStorage.setItem('screen_share_requested', 'true');
      // Note: Trial cooldown timestamp will be set when trial ENDS, not here
      
      // Store stream ID or track ID so we can reference it
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        // We'll need to pass the stream to the chat interface
        // Store it temporarily in window object since localStorage can't store objects
        window.pendingScreenStream = stream;
      }
      
      // Delay navigation slightly to ensure focus is fully restored
      setTimeout(() => {
        // One more focus attempt before navigation
        if (window.focus) window.focus();
      // console.log('=== Navigating to /chat after screen share ===');
        // Navigate FIRST before closing modal
        navigate('/chat', { replace: true });
        // Then close modal
        setTimeout(() => {
          handleClose();
        }, 100);
      }, 400);
    } catch (err) {
      console.error('Screen sharing was denied or failed:', err);
      // If user cancels screen sharing, show an error or just don't navigate
      if (err.name === 'NotAllowedError') {
        setScreenShareError('Screen sharing is required to start the interview. Please allow screen sharing and try again.');
      } else {
        setScreenShareError('Failed to start screen sharing: ' + err.message);
      }
      // Don't navigate if screen sharing fails
    }
  };

  const isStep1Valid = formData.company.trim() !== '';

  const formatDuration = (ms) => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    return `${pad(minutes)}:${pad(seconds)}`;
  };

  const isTrialCooldownActive = isTrial && trialCooldownUntil && cooldownRemainingMs > 0;

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div className="trial-modal-overlay" onClick={handleOverlayClick}>
      <div className="trial-modal">
        <button className="trial-modal-close" onClick={handleClose}>
          ✕
        </button>

        {step === 1 && (
          <>
            <div className="trial-modal-header">
              <h2 className="trial-modal-title">Language & Instructions</h2>
              <p className="trial-modal-subtitle">
                Choose your language and provide special instructions for the AI when generating answers.
              </p>
            </div>

            <div className="trial-modal-body">
              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">🌐</span>
                  Language
                </label>
                <select
                  className="form-select"
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                >
                  <option value="ar">Arabic</option>
                  <option value="ba">Bashkir</option>
                  <option value="eu">Basque</option>
                  <option value="be">Belarusian</option>
                  <option value="bn">Bengali</option>
                  <option value="bg">Bulgarian</option>
                  <option value="yue">Cantonese</option>
                  <option value="ca">Catalan</option>
                  <option value="hr">Croatian</option>
                  <option value="cs">Czech</option>
                  <option value="da">Danish</option>
                  <option value="nl">Dutch</option>
                  <option value="en">English</option>
                  <option value="eo">Esperanto</option>
                  <option value="et">Estonian</option>
                  <option value="fi">Finnish</option>
                  <option value="fr">French</option>
                  <option value="gl">Galician</option>
                  <option value="de">German</option>
                  <option value="el">Greek</option>
                  <option value="he">Hebrew</option>
                  <option value="hi">Hindi</option>
                  <option value="hu">Hungarian</option>
                  <option value="ia">Interlingua</option>
                  <option value="ga">Irish</option>
                  <option value="it">Italian</option>
                  <option value="id">Indonesian</option>
                  <option value="ja">Japanese</option>
                  <option value="ko">Korean</option>
                  <option value="lv">Latvian</option>
                  <option value="lt">Lithuanian</option>
                  <option value="ms">Malay</option>
                  <option value="mt">Maltese</option>
                  <option value="zh">Mandarin Chinese</option>
                  <option value="mr">Marathi</option>
                  <option value="mn">Mongolian</option>
                  <option value="no">Norwegian</option>
                  <option value="fa">Persian (Farsi)</option>
                  <option value="pl">Polish</option>
                  <option value="pt">Portuguese</option>
                  <option value="ro">Romanian</option>
                  <option value="ru">Russian</option>
                  <option value="sk">Slovakian</option>
                  <option value="sl">Slovenian</option>
                  <option value="es">Spanish</option>
                  <option value="sw">Swahili</option>
                  <option value="sv">Swedish</option>
                  <option value="ta">Tamil</option>
                  <option value="tl">Tagalog (Filipino)</option>
                  <option value="th">Thai</option>
                  <option value="tr">Turkish</option>
                  <option value="ur">Urdu</option>
                  <option value="ug">Uyghur</option>
                  <option value="uk">Ukrainian</option>
                  <option value="vi">Vietnamese</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">🤖</span>
                  AI Model <span className="optional-badge">(Optional)</span>
                </label>
                <select
                  className="form-select"
                  value={formData.aiModel}
                  onChange={(e) => handleInputChange('aiModel', e.target.value)}
                >
                  {Array.isArray(gptModels) && gptModels.length > 0 ? (
                    gptModels.map(model => (
                      <option key={model} value={model}>{getModelDisplayName(model)}</option>
                    ))
                  ) : (
                    <>
                      <option value="GPT-4.1 (Smarter)">GPT-4.1 (Smarter)</option>
                      <option value="GPT-4.0 (Faster)">GPT-4.0 (Faster)</option>
                      <option value="GPT-3.5 (Budget)">GPT-3.5 (Budget)</option>
                    </>
                  )}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">📏</span>
                  Answer Length <span className="optional-badge">(Optional)</span>
                </label>
                <select
                  className="form-select"
                  value={formData.answerMode}
                  onChange={(e) => handleInputChange('answerMode', e.target.value)}
                >
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="depth">Detailed</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">🏢</span>
                  Company
                </label>
                <input
                  type="text"
                  className={`form-input ${errors.company ? 'form-input-error' : ''}`}
                  placeholder={errors.company || "Enter company name"}
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                />
              </div>
            </div>

            <div className="trial-modal-footer">
              <button 
                className="btn-modal-trial btn-modal-primary" 
                onClick={handleNext}
                style={{ marginLeft: 'auto' }}
              >
                Next →
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className="trial-modal-header">
              <h2 className="trial-modal-title">Select Resume</h2>
              <p className="trial-modal-subtitle">
                Choose a resume to help the AI provide more personalized answers based on your experience.
              </p>
            </div>

            <div className="trial-modal-body">
              <div className="form-group">
                <label className="form-label">
                  <span className="form-label-icon">📄</span>
                  Resume
                </label>
                <div className="resume-select-container">
                  <div className="resume-select-wrapper">
                    {resumes.length === 0 ? (
                      <div className={`resume-select-empty ${errors.resume ? 'resume-select-error' : ''}`}>
                        {errors.resume || 'No resumes found'}
                      </div>
                    ) : (
                      <select
                        className={`form-select ${errors.resume ? 'form-select-error' : ''}`}
                        value={formData.resumeId || ''}
                        onChange={(e) => handleInputChange('resumeId', e.target.value)}
                      >
                        <option value="">{errors.resume || 'Select a resume...'}</option>
                        {resumes.map((resume) => (
                          <option key={resume.id} value={resume.id}>
                            {resume.filename}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <button 
                    className="btn-add-resume" 
                    onClick={handleAddResume}
                    title="Upload new resume"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>

            {screenShareError && (
              <div className="screen-share-error">
                <span className="screen-share-error-icon">⚠️</span>
                <span>{screenShareError}</span>
              </div>
            )}

            <div className="trial-modal-footer">
              <button 
                className="btn-modal-trial btn-modal-secondary" 
                onClick={handleBack}
              >
                ← Back
              </button>
              <button 
                className="btn-modal-trial btn-modal-primary" 
                onClick={handleNext}
              >
                Start Interview →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
